import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import CookieBanner from "./CookieBanner";
import CookiePreferencesDialog from "./CookiePreferencesDialog";
import {
  onOpenCookieSettings,
  readConsent,
  saveConsent,
  subscribeConsent,
} from "../../utils/cookieConsent";
import {
  disableMetaPixel,
  enableMetaPixel,
  isPixelHost,
  isTrackedLocation,
  metaPixelId,
  trackPageView,
} from "../../utils/metaPixel";

/**
 * Asks for cookie consent and makes the Meta Pixel obey the answer.
 *
 * Mounted once, inside the router in App.jsx — never in the booking widget or
 * the site preview, which index.jsx mounts instead of App: those run framed in
 * pages that are not ours to put a banner on.
 *
 * The banner appears only where the pixel could run (our own public pages,
 * `isTrackedLocation`), and only where a pixel can load — a production build on
 * production's address (`isPixelHost`; UAT runs the same build and asks
 * nothing). A question about something the site does not do is noise. The dev server is
 * the exception: it has no pixel ID, so it loads nothing from Meta whatever the
 * answer, but it shows the banner, so the banner can be seen and worked on
 * where everything else is. (The e2e run answers it up front, through
 * `storageState` in playwright.config.js.) The preferences dialog opens from
 * anywhere, so the footer link always works.
 */
const bannerApplies = () =>
  (Boolean(metaPixelId()) && isPixelHost()) || import.meta.env.DEV;
const CookieConsent = () => {
  const location = useLocation();
  const [consent, setConsent] = useState(readConsent);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const marketing = consent?.marketing === true;

  useEffect(() => subscribeConsent(setConsent), []);
  useEffect(() => onOpenCookieSettings(() => setPreferencesOpen(true)), []);

  // Refused, withdrawn, never given or run out: the pixel goes quiet and its
  // cookies go, in every tab (subscribeConsent hears the others).
  useEffect(() => {
    if (!marketing) disableMetaPixel();
  }, [marketing]);

  // One PageView per page we own, with consent. Keyed on the path alone: the
  // search page rewrites its query string as the visitor types, and that is not
  // a new page. The pixel drops a repeat of the same address by itself, so
  // StrictMode's second run sends nothing.
  useEffect(() => {
    if (!marketing || !isTrackedLocation(location)) return;
    if (enableMetaPixel()) trackPageView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketing, location.pathname]);

  const decide = useCallback((answer) => {
    saveConsent(answer);
    setPreferencesOpen(false);
  }, []);

  const acceptAll = useCallback(() => decide({ marketing: true }), [decide]);
  const rejectOptional = useCallback(() => decide({ marketing: false }), [decide]);
  const closePreferences = useCallback(() => setPreferencesOpen(false), []);

  const showBanner =
    bannerApplies() &&
    consent === null &&
    !preferencesOpen &&
    isTrackedLocation(location);

  return (
    <>
      {showBanner && (
        <CookieBanner
          onAcceptAll={acceptAll}
          onCustomize={() => setPreferencesOpen(true)}
          onRejectOptional={rejectOptional}
        />
      )}
      <CookiePreferencesDialog
        open={preferencesOpen}
        marketing={marketing}
        onClose={closePreferences}
        onSave={decide}
        onAcceptAll={acceptAll}
        onRejectOptional={rejectOptional}
      />
    </>
  );
};

export default CookieConsent;
